import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import galleryData from "../data/barbequeen-outlet-gallery-list.json";
import SEO from "../components/SEO";
import { galleryPageSchema } from "../data/schemas";
import FAQSection from "../components/FAQSection";
import { galleryFaqs } from "../data/faqs";

const outlets = galleryData.outlets;

/* ── Derive unique cities for tabs ── */
const ALL_CITIES = ["All", ...Array.from(new Set(outlets.map((o) => o.city))).sort()];

/* ── Gallery Card ── */
const GalleryCard = ({ item }) => {
  return (
    <Link
      to="/outlet-gallery"
      className="group relative bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col"
    >
      {/* Thumbnail Container */}
      <div className="relative overflow-hidden" style={{ paddingBottom: "70%" }}>
        <img
          src={item.thumbnail}
          alt={item.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=800&auto=format&fit=crop";
          }}
        />
        {/* Dark overlay on hover */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Zoom/Link Icon */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </div>
        </div>

        {/* City badge */}
        <span className="absolute top-3 left-3 px-3 py-1 bg-[#9b1c2e] text-white text-[10px] font-bold tracking-wider uppercase rounded-full shadow-md">
          {item.city}
        </span>
      </div>

      {/* Card Content */}
      <div className="p-5 flex-1 flex flex-col justify-between">
        <div>
          <h3
            className="text-[16px] font-bold text-gray-900 leading-snug group-hover:text-[#9b1c2e] transition-colors"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {item.name}
          </h3>
          <p className="text-xs text-gray-400 mt-1 font-sans">
            Explore photos of our {item.name} outlet.
          </p>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs font-bold tracking-widest uppercase text-[#9b1c2e] group-hover:gap-2 flex items-center gap-1.5 transition-all duration-200">
            View Gallery
            <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </span>
          <span className="text-xs text-gray-300 font-sans">#{item.id}</span>
        </div>
      </div>
    </Link>
  );
};

/* ── Main Page Component ── */
const OutletGalleryPage = () => {
  const [activeCity, setActiveCity] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() =>
    outlets.filter((o) => {
      const matchCity = activeCity === "All" || o.city === activeCity;
      const q = search.toLowerCase();
      const matchSearch = !q || o.name.toLowerCase().includes(q) || o.city.toLowerCase().includes(q);
      return matchCity && matchSearch;
    }),
    [activeCity, search]
  );

  return (
    <div className="min-h-screen bg-[#f9f5f0]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        title="Barbequeen Outlet Gallery | Restaurant Photos & Interiors"
        description="Browse the Barbequeen outlet gallery for photos of our dining spaces, live grills, and signature dishes across Salem, Coimbatore, Erode, Krishnagiri & Hosur."
        keywords="Barbequeen outlet gallery, Barbequeen photos, Barbequeen restaurant interiors, Barbequeen Salem gallery, Barbequeen ambience"
        robots="index, follow, max-image-preview:large"
        canonical="https://barbequeen.com/outlet-gallery"
        og={{
          type: "website",
          url: "https://barbequeen.com/outlet-gallery",
          title: "Barbequeen Outlet Gallery | Restaurant Photos & Interiors",
          description: "Photos of Barbequeen's dining spaces, live grills, and signature dishes across Salem, Coimbatore, Erode, Krishnagiri & Hosur.",
          image: "https://barbequeen.com/images/outlet-gallery-og.jpg",
          imageWidth: "1200",
          imageHeight: "630",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
        schema={galleryPageSchema}
      />

      {/* ── Hero Banner ── */}
      <section className="relative overflow-hidden" style={{ minHeight: "340px" }}>
        <img
          src="https://images.unsplash.com/photo-1498654896293-37aacf113fd9?q=80&w=1600&auto=format&fit=crop"
          alt="Outlet Gallery"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(10,0,2,0.72) 0%, rgba(10,0,2,0.82) 100%)" }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">Our Spaces</p>
          <h1 className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
            Outlet Gallery
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed mb-8">
            Take a visual tour of our various Barbequeen outlets and dining spaces.
          </p>

          {/* Search bar */}
          <div className="relative max-w-md mx-auto">
            <input
              type="text"
              placeholder="Search by city or area…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-5 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#9b1c2e] focus:bg-white/15 transition-all duration-200 backdrop-blur-sm"
            />
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── City Filter Tabs ── */}
      <div className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-none">
            {ALL_CITIES.map((city) => (
              <button
                key={city}
                onClick={() => setActiveCity(city)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold tracking-wide uppercase transition-all duration-200 border ${
                  activeCity === city
                    ? "bg-[#9b1c2e] text-white border-[#9b1c2e]"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                }`}
              >
                {city}
                {city === "All" && <span className="ml-1 opacity-60">({outlets.length})</span>}
              </button>
            ))}
            <span className="ml-auto flex-shrink-0 text-xs text-gray-400 pl-4 whitespace-nowrap">
              {filtered.length} gallery item{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-12">
        {filtered.length === 0 ? (
          <div className="text-center py-24 space-y-4">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-gray-500">No galleries found matching your criteria.</p>
            <button onClick={() => { setActiveCity("All"); setSearch(""); }} className="text-xs font-bold tracking-widest uppercase text-[#9b1c2e] hover:underline">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <GalleryCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      <FAQSection faqs={galleryFaqs} />
    </div>
  );
};

export default OutletGalleryPage;

